import {
  Dropdown,
  FormLayout,
  TextInput,
  MultiSelectStoreDropdown,
} from 'components/common'
import {
  CommonFormType,
  activeOptions,
  aggregatorFormFields,
  authTypes,
  authenticationOptions,
  getNestErrorMessage,
} from '../utils'
import JSONInputText from './jsonInputText'
import { Control } from 'react-hook-form'
import { useCallback, useEffect } from 'react'

const {
  API_ACTIVE,
  API_URL,
  API_STORES,
  API_AUTH_TYPE,
  API_AUTH_URL,
  API_AUTH_TOKEN_KEY,
  API_AUTH_USERNAME,
  API_AUTH_PASSWORD,
  API_AUTH_API_KEY,
  API_AUTH_API_VALUE,
  API_AUTH_REQ_SCHEMA,
  API_PRODUCT_SCHEMA,
  API_ROOT_SCHEMA,
  API_AUTH_TOKEN_EXPIRY,
  API_MAX_CONCURRENT_STORES,
  API_ITEMS_PER_REQUEST,
  API_MAX_PARALLEL_ITEM_BATCHES,
} = aggregatorFormFields

interface ApiConfigurationFormProps extends CommonFormType {
  currentAuthMode: string
  control: Control<any>
}

const getAggregatorDefaultValue = (
  aggregator: Record<string, any>,
  obj: Record<string, any>,
) => {
  return {
    key: obj.parentKey + '.' + obj.key,
    value: aggregator?.[obj.parentKey]?.[obj.key] || '',
  }
}

function ApiConfigurationForm({
  register,
  errors,
  setValue,
  aggregator,
  apiIsActive,
  currentAuthMode,
  control,
}: ApiConfigurationFormProps) {
  // Clear Bearer Auth Fields
  const clearBearerAuth = useCallback(() => {
    const apiAuthUrl = getAggregatorDefaultValue(aggregator, API_AUTH_URL)
    const apiAuthTokenKey = getAggregatorDefaultValue(
      aggregator,
      API_AUTH_TOKEN_KEY,
    )
    const apiAuthReqSchema = getAggregatorDefaultValue(
      aggregator,
      API_AUTH_REQ_SCHEMA,
    )
    const apiAuthTokenExpiry = getAggregatorDefaultValue(
      aggregator,
      API_AUTH_TOKEN_EXPIRY,
    )

    setValue(apiAuthUrl.key, apiAuthUrl.value)
    setValue(apiAuthTokenKey.key, apiAuthTokenKey.value)
    setValue(apiAuthReqSchema.key, apiAuthReqSchema.value)
    setValue(apiAuthTokenExpiry.key, apiAuthTokenExpiry.value)
  }, [setValue, aggregator])

  // Clear Basic Auth Fields
  const clearBasicAuth = useCallback(() => {
    const apiAuthUsername = getAggregatorDefaultValue(
      aggregator,
      API_AUTH_USERNAME,
    )
    const apiAuthPassword = getAggregatorDefaultValue(
      aggregator,
      API_AUTH_USERNAME,
    )
    setValue(apiAuthUsername.key, apiAuthUsername.value)
    setValue(apiAuthPassword.key, apiAuthPassword.value)
  }, [setValue, aggregator])

  // Clear API Auth Fields
  const clearApiAuth = useCallback(() => {
    const apiAuthApikey = getAggregatorDefaultValue(
      aggregator,
      API_AUTH_API_KEY,
    )
    setValue(apiAuthApikey.key, apiAuthApikey.value)
  }, [setValue, aggregator])

  useEffect(() => {
    if (currentAuthMode === authTypes.BEARER) {
      clearApiAuth()
      clearBasicAuth()
    }

    if (currentAuthMode === authTypes.BASIC) {
      clearBearerAuth()
      clearApiAuth()
    }

    if (currentAuthMode === authTypes.API_KEY) {
      clearBearerAuth()
      clearBasicAuth()
    }
  }, [currentAuthMode])

  return (
    <>
      <h3 className="title">API Configuration</h3>
      <FormLayout>
        <div className="flex flex-col gap-2">
          <div className="gap-5 mid-md:flex mid-md:w-2/3">
            {/* API Status */}
            <Dropdown
              required
              defaultValue={aggregator?.api?.isEnable ?? false}
              setValue={setValue}
              options={activeOptions}
              label={API_ACTIVE.label}
              name={API_ACTIVE.parentKey + '.' + API_ACTIVE.key}
              error={getNestErrorMessage(errors, API_ACTIVE)}
            />

            {/* API Stores */}
            <MultiSelectStoreDropdown
              required={apiIsActive}
              resetForm
              defaultSelected={aggregator?.api?.stores}
              setValue={setValue}
              label={API_STORES.label}
              name={API_STORES.parentKey + '.' + API_STORES.key}
              error={getNestErrorMessage(errors, API_STORES)}
            />
          </div>

          <div className="gap-5 mid-md:flex mid-md:w-full">
            {/* Max Concurrent Stores */}
            <TextInput
              type="number"
              required={apiIsActive}
              register={register}
              label={API_MAX_CONCURRENT_STORES.label}
              name={
                API_MAX_CONCURRENT_STORES.parentKey +
                '.' +
                API_MAX_CONCURRENT_STORES.key
              }
              hintText="How many stores of this aggregator are synced at the same time (e.g. 5 stores in parallel, each store processed one at a time)"
              error={getNestErrorMessage(errors, API_MAX_CONCURRENT_STORES)}
            />

            {/* Items Per Request */}
            <TextInput
              type="number"
              required={apiIsActive}
              register={register}
              label={API_ITEMS_PER_REQUEST.label}
              name={
                API_ITEMS_PER_REQUEST.parentKey +
                '.' +
                API_ITEMS_PER_REQUEST.key
              }
              hintText="For a single store + aggregator combination, the item list (which can be ~1,00,000 items) is split into batches — this sets how many items are sent in one API request (batch size)"
              error={getNestErrorMessage(errors, API_ITEMS_PER_REQUEST)}
            />

            {/* Max Parallel Item Batches */}
            <TextInput
              type="number"
              required={apiIsActive}
              register={register}
              label={API_MAX_PARALLEL_ITEM_BATCHES.label}
              name={
                API_MAX_PARALLEL_ITEM_BATCHES.parentKey +
                '.' +
                API_MAX_PARALLEL_ITEM_BATCHES.key
              }
              hintText="For that same store + aggregator combination, how many of those item batches (from Items Per Request above) are sent to the aggregator at the same time, in parallel"
              error={getNestErrorMessage(errors, API_MAX_PARALLEL_ITEM_BATCHES)}
            />
          </div>

          <p className="mb-2 mt-4 text-sm font-bold text-sidebar-primary">
            Authentication
          </p>

          <div className="mid-md:w-1/3 mid-md:pr-4">
            <Dropdown
              required={apiIsActive}
              defaultValue={aggregator?.api?.authType || authTypes.BEARER}
              setValue={setValue}
              options={authenticationOptions}
              label={API_AUTH_TYPE.label}
              name={API_AUTH_TYPE.parentKey + '.' + API_AUTH_TYPE.key}
              error={getNestErrorMessage(errors, API_AUTH_TYPE)}
            />
          </div>

          {currentAuthMode === authTypes.BEARER && (
            <>
              <div className="gap-5 mid-md:flex">
                {/* Auth URL */}
                <TextInput
                  required={apiIsActive}
                  register={register}
                  label={API_AUTH_URL.label}
                  name={API_AUTH_URL.parentKey + '.' + API_AUTH_URL.key}
                  error={getNestErrorMessage(errors, API_AUTH_URL)}
                />

                {/* response token key */}
                <TextInput
                  required={apiIsActive}
                  register={register}
                  label={API_AUTH_TOKEN_KEY.label}
                  name={
                    API_AUTH_TOKEN_KEY.parentKey + '.' + API_AUTH_TOKEN_KEY.key
                  }
                  error={getNestErrorMessage(errors, API_AUTH_TOKEN_KEY)}
                />

                {/* Auth Token Expiry */}
                <TextInput
                  type="number"
                  register={register}
                  label={API_AUTH_TOKEN_EXPIRY.label}
                  name={
                    API_AUTH_TOKEN_EXPIRY.parentKey +
                    '.' +
                    API_AUTH_TOKEN_EXPIRY.key
                  }
                  hintText="Time in minutes after which the auth token expires and a new one is fetched"
                  error={getNestErrorMessage(errors, API_AUTH_TOKEN_EXPIRY)}
                />
              </div>

              {/* Login Request Schema */}
              <div className="mid-md:w-1/2 mid-md:pr-4">
                <JSONInputText
                  defaultValue={aggregator?.api?.authRequestSchema}
                  required={apiIsActive}
                  control={control}
                  label={API_AUTH_REQ_SCHEMA.label}
                  name={
                    API_AUTH_REQ_SCHEMA.parentKey +
                    '.' +
                    API_AUTH_REQ_SCHEMA.key
                  }
                  error={getNestErrorMessage(errors, API_AUTH_REQ_SCHEMA)}
                  placeholder="Enter Your Schema Here in JSON format..."
                />
              </div>
            </>
          )}

          {currentAuthMode === authTypes.BASIC && (
            <div className="gap-5 mid-md:flex">
              {/* Auth:Basic Username */}
              <TextInput
                required={apiIsActive}
                register={register}
                label={API_AUTH_USERNAME.label}
                name={API_AUTH_USERNAME.parentKey + '.' + API_AUTH_USERNAME.key}
                error={getNestErrorMessage(errors, API_AUTH_USERNAME)}
              />

              {/* Auth:Basic Password */}
              <TextInput
                type="password"
                required={apiIsActive}
                register={register}
                label={API_AUTH_PASSWORD.label}
                name={API_AUTH_PASSWORD.parentKey + '.' + API_AUTH_PASSWORD.key}
                error={getNestErrorMessage(errors, API_AUTH_PASSWORD)}
              />
            </div>
          )}

          {/* Auth: API Key Based */}
          {currentAuthMode === authTypes.API_KEY && (
            <div className="gap-5 mid-md:flex">
              <TextInput
                required={apiIsActive}
                register={register}
                label={API_AUTH_API_KEY.label}
                name={API_AUTH_API_KEY.parentKey + '.' + API_AUTH_API_KEY.key}
                error={getNestErrorMessage(errors, API_AUTH_API_KEY)}
              />
              <TextInput
                required={apiIsActive}
                register={register}
                label={API_AUTH_API_VALUE.label}
                name={
                  API_AUTH_API_VALUE.parentKey + '.' + API_AUTH_API_VALUE.key
                }
                error={getNestErrorMessage(errors, API_AUTH_API_VALUE)}
              />
            </div>
          )}

          <hr className="my-6" />

          <p className="mb-2 text-sm font-bold text-sidebar-primary">
            Aggregator API
          </p>

          {/* API URL */}
          <TextInput
            required={apiIsActive}
            register={register}
            label={API_URL.label}
            name={API_URL.parentKey + '.' + API_URL.key}
            error={getNestErrorMessage(errors, API_URL)}
          />

          <div className="gap-5 mid-md:flex">
            <JSONInputText
              defaultValue={aggregator?.api?.rootSchema}
              required={apiIsActive}
              control={control}
              label={API_ROOT_SCHEMA.label}
              name={API_ROOT_SCHEMA.parentKey + '.' + API_ROOT_SCHEMA.key}
              error={getNestErrorMessage(errors, API_ROOT_SCHEMA)}
              placeholder="Enter Your Schema Here in JSON format..."
            />

            <JSONInputText
              defaultValue={aggregator?.api?.productSchema}
              required={apiIsActive}
              control={control}
              label={API_PRODUCT_SCHEMA.label}
              name={API_PRODUCT_SCHEMA.parentKey + '.' + API_PRODUCT_SCHEMA.key}
              error={getNestErrorMessage(errors, API_PRODUCT_SCHEMA)}
              placeholder="Enter Your Schema Here in JSON format..."
            />
          </div>
        </div>
      </FormLayout>
    </>
  )
}

export default ApiConfigurationForm
